/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import Card from '../../../components/Card/Card';
import CardBody from '../../../components/Card/CardBody';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { configDefault } from '../../../core/config';
import { useDispatch } from 'react-redux';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import { DISCOUNT_CONSTANTS } from '../../../modules/discounts/reducers';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import EditButton from '../../../components/Buttons/EditButton';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import withStyles from '@material-ui/core/styles/withStyles';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import DeleteButton from '../../../components/Buttons/DeleteButton';
import { AlertDialog } from '../../lib/dialog/AlertDialog';
import { tariffConstants } from '../../../modules/tariffs/actions';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import TableFooter from '@material-ui/core/TableFooter';
import Pager from '../../../components/Adescom/PagerRange';
import WelcomeSmsZoneForm from '../WelcomeSmsConfiguration/WelcomeSmsZoneForm';

interface ComponentZoneConfigurationInterface {
    classes: any;
}

const ComponentZoneConfiguration = (props: ComponentZoneConfigurationInterface): React.ReactElement => {
    const [addMode, setAddMode] = useState<boolean>(false);
    const [zoneAddMode, setZoneAddMode] = useState<boolean>(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [selectedZone, setSelectedZone] = useState<string | null>(null);
    const [zones, setZones] = useState<any>(null);
    const [editedZone, setEditedZone] = useState<any>(null);
    const [filters, setFilters] = useState<Array<Filter>>([{ name: 'customZoneType', value: 2 }]);
    const [orders, setOrders] = useState<Array<Order>>([]);
    const [selectedIdToDelete, setSelectedIdToDelete] = useState<number | null>(null);
    const [deleteMessage, setDeleteMessage] = useState<string | null>(null);

    const ZONE_URI = '/zones';
    const { classes } = props;

    const dispatch = useDispatch();

    const tableStickyHeader = { root: classes.tableStickyHeader };

    const loadZones = useCallback(
        (uri?: string | null) => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(ZONE_URI);
            } else {
                // Quick fix, We need to put "?" before "&" because we do not use filters here, so uri does not have it. When we dont have "?" in uri system does not recognise path
                uri = uri.replace(/&/g, '?&');
            }
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                DISCOUNT_CONSTANTS.LIST,
                filters,
                orders,
                (data: any) => {
                    setZones(data);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        },
        [dispatch, filters, orders]
    );

    const addNewZone = (): void => {
        setZoneAddMode(true);
    };

    const makeEdit = (params: any): void => {
        setAddMode(true);
        setEditId(params.item.id);
        setSelectedZone(params.item.zoneId);
    };

    const makeZoneEdit = (params: any): void => {
        setZoneAddMode(true);
        setEditedZone(params.item);
    };

    const makeDelete = (params: { id: number }): void => {
        setSelectedIdToDelete(params.id);
        setDeleteMessage('notifications:h.welcome_sms_delete_message');
    };
    const makeZoneDelete = (params: { id: number }): void => {
        setSelectedIdToDelete(params.id);
        setDeleteMessage('notifications:h.zone_delete_message');
    };

    const confirmDelete = (): void => {
        if (deleteMessage === 'notifications:h.zone_delete_message') {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.deleteCall(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`${ZONE_URI}/${selectedIdToDelete}`, configDefault.PLATFORM_API_PREFIX)),
                tariffConstants.DELETE,
                (data: any) => {
                    setSelectedIdToDelete(null);
                    setDeleteMessage(null);
                    if (!data) {
                        NotificationHelper.notifyError('notifications:notify.zone_delete_error', dispatch);
                    } else {
                        NotificationHelper.notifySuccess('notifications:notify.zone_delete_success', dispatch);
                        loadZones();
                    }
                },
                { dispatchAfterCallback: false }
            );
            return;
        }
    };

    useEffect(() => {
        loadZones();
    }, [loadZones]);

    if (zoneAddMode) {
        return (
            <WelcomeSmsZoneForm classes={classes} editZone={editedZone} setZoneAddMode={setZoneAddMode} loadZones={loadZones} customZoneType={2} />
        );
    } else {
        return (
            <GridContainer spacing={2}>
                <GridItem>
                    <CustomTextButtom
                        size={'lg'}
                        color={'primary'}
                        callback={addNewZone}
                        callbackParam={{}}
                        label={'notifications:welcome_sms.add_new_zone'}
                    />
                </GridItem>
                <GridItem xs={12}>
                    <Card>
                        <CardHeader icon={<AccountTreeIcon />} trans="notifications:welcome_sms.zones" />
                        <CardBody>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 30 }}>
                                            <I18n trans="notifications:welcome_sms.actions" />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} padding="none" align="center">
                                            <I18n trans="notifications:h.zone" />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {zones &&
                                        zones['hydra:member'].map((el: any) => {
                                            return (
                                                <TableRow key={el.id}>
                                                    <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 30 }}>
                                                        <EditButton callback={makeZoneEdit} callbackParam={{ item: el }} />
                                                        <DeleteButton callback={makeZoneDelete} callbackParam={{ id: el.id }} />
                                                    </TableCell>
                                                    <TableCell classes={tableStickyHeader} padding="none" align="center">
                                                        {el.mainname}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell colSpan={10}>
                                            {zones && zones['hydra:view'] && <Pager callback={loadZones} views={zones} />}
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </CardBody>
                    </Card>
                </GridItem>
                <GridItem xs={12}>
                    {deleteMessage && (
                        <AlertDialog
                            open={!!deleteMessage}
                            titleDialog="notifications:h.delete_confirmation"
                            displayMessage={deleteMessage}
                            closeCallback={(): void => {
                                setDeleteMessage(null);
                                setSelectedIdToDelete(null);
                            }}
                            confirmCallback={confirmDelete}
                        />
                    )}
                </GridItem>
            </GridContainer>
        );
    }
};
export default withStyles(oplAppStyle)(ComponentZoneConfiguration);
